import React from 'react'
import { PriceSetting, AdjustmentType, DynamicInterval, OverrideType } from 'products/pricing/pricingService'
import { Currency } from 'uiComponents/money/moneyHoc'
import { SecondaryText } from 'uiComponents/typography'
import { Row, Column, ContainerWithStatus } from 'uiComponents/pageElements'
import { ValidationNotice } from 'products/components/validationNotice'
import { Radio } from 'uiComponents/input/radio'
import Infotip from 'uiComponents/infotip'
import { SetAdjustmentFunc } from 'products/pricing/exceptions/exceptionForm'
import { AdjustRuleItem } from './ruleItem'
import { DynamicIntervals } from './dynamicIntervals'
import { Adjustment, RadioWrapper, HighlightedText } from './shared'
import { useTranslation } from 'translations'

interface RulesForStaticProps {
    currency: Currency
    adjustment: Adjustment
    intervals: DynamicInterval[] | null
    setIntervals: (intervals: DynamicInterval[] | null) => void
    setAdjustment: SetAdjustmentFunc
    activatePriceSettingRule: (priceSetting: PriceSetting, activate: boolean) => void
    validate: boolean
    areIntervalsValid: boolean
    doIntervalsOverlap: boolean
    overrideType: OverrideType | null
    onOverrideTypeChange: (type: OverrideType) => void
}

export function RulesForStatic(props: RulesForStaticProps) {
    const { t } = useTranslation()
    function changeStrategy(e: React.ChangeEvent<HTMLInputElement>) {
        const s = e.target.value as OverrideType
        props.onOverrideTypeChange(s)
    }

    function setAdjustment(priceSetting: PriceSetting, value: number, type: AdjustmentType) {
        props.setAdjustment(priceSetting, value, type)
        props.activatePriceSettingRule(priceSetting, true)
    }

    const { currency, adjustment, validate, intervals, areIntervalsValid, overrideType, doIntervalsOverlap } = props
    const ruleNotValid = !intervals && !adjustment.on && validate
    const intervalsWithNull = intervals && intervals.find((i) => i.endDay === null || i.startDay === null)
    const intervalsNotValid = validate && intervals && (!areIntervalsValid || intervalsWithNull)
    return (
        <>
            {ruleNotValid && <ValidationNotice large>{t('products.pricing.exceptions.rules.selectRule')}</ValidationNotice>}
            <ContainerWithStatus
                status={ruleNotValid || intervalsNotValid || doIntervalsOverlap ? 'error' : 'success'}
                style={{ flexDirection: 'column' }}
            >
                <Row style={{ marginBottom: '2em' }}>
                    <RadioWrapper style={{ marginLeft: '.5em' }}>
                        <Radio
                            id="adjustStrategy"
                            name="strategy"
                            value="adjust"
                            onChange={changeStrategy}
                            defaultChecked={overrideType === 'adjust'}
                        />
                        <HighlightedText>{t('products.pricing.exceptions.rules.staticStrategy')}</HighlightedText>
                        <Infotip pointer="left" maxWidth="18em">
                            {t('products.pricing.exceptions.rules.staticStrategyInfo')}
                        </Infotip>
                    </RadioWrapper>
                    <SecondaryText style={{ margin: '0 2em' }}>{t('products.pricing.exceptions.rules.or')}</SecondaryText>
                    <RadioWrapper>
                        <Radio
                            id="dynamicStrategy"
                            name="strategy"
                            value="dynamic"
                            onChange={changeStrategy}
                            defaultChecked={overrideType === 'dynamic'}
                        />
                        <HighlightedText>{t('products.pricing.exceptions.rules.dynamicStrategy')}</HighlightedText>
                        <Infotip pointer="left" maxWidth="18em">
                            {t('products.pricing.exceptions.rules.dynamicStrategyInfo')}
                        </Infotip>
                    </RadioWrapper>
                </Row>
                {overrideType === 'adjust' && (
                    <>
                        <Row>
                            <Column>
                                <div style={{ marginLeft: '0.5em' }}>{t('products.pricing.exceptions.rules.ruleForPrice')}</div>
                            </Column>
                        </Row>
                        <Row>
                            <AdjustRuleItem
                                isOn={true}
                                priceSetting={adjustment.name}
                                adjustmentType={adjustment.type}
                                adjustmentSize={adjustment.value}
                                currency={currency}
                                handleRuleChange={setAdjustment}
                                validate={validate}
                            />
                        </Row>
                    </>
                )}
                {overrideType === 'dynamic' && (
                    <>
                        <Row>
                            <Column>
                                <div style={{ marginLeft: '0.5em' }}>{t('products.pricing.exceptions.rules.intervals')}</div>
                            </Column>
                        </Row>
                        <Row>
                            <DynamicIntervals
                                intervals={intervals}
                                currency={currency}
                                setIntervals={props.setIntervals}
                                validate={validate}
                            />
                        </Row>
                    </>
                )}
            </ContainerWithStatus>
            {ruleNotValid && (
                <ValidationNotice className="validation-message-visible" style={{ marginTop: '1em' }}>
                    {t('products.pricing.exceptions.rules.setInterval')}
                </ValidationNotice>
            )}
            {doIntervalsOverlap && (
                <ValidationNotice className="validation-message-visible" style={{ marginTop: '1em' }}>
                    {t('products.pricing.exceptions.rules.intervalsOverlap')}
                </ValidationNotice>
            )}
            {intervalsNotValid && (
                <ValidationNotice className="validation-message-visible">{t('products.pricing.exceptions.rules.intervalsIncomplete')}</ValidationNotice>
            )}
        </>
    )
}
